export const LINEAR_BASE_URL = "https://linear.app/getindigo/team/GET/new";
const VALIDATION_TICKET_PROJECT = "Validations (History, Records, Assessment)";

export enum ValidationType {
  History = "History",
  MedicalRecordReview = "Medical Record Review",
  Assessment = "Assessment",
}

const VALIDATION_TICKET_ASSIGNEES: Record<ValidationType, string> = {
  [ValidationType.History]: "duo",
  [ValidationType.MedicalRecordReview]: "haoyun",
  [ValidationType.Assessment]: "james",
};

// Short tag prefixed to user-feedback ticket titles, e.g. "[MRR validation] …".
const VALIDATION_TICKET_TITLE_TAG: Record<ValidationType, string> = {
  [ValidationType.History]: "History validation",
  [ValidationType.MedicalRecordReview]: "MRR validation",
  [ValidationType.Assessment]: "Assessment validation",
};

const MAX_DESCRIPTION_LENGTH = 1500;
const MAX_TITLE_TEXT_LENGTH = 80;

// Linear priority: 0=None, 1=Urgent, 2=High, 3=Medium, 4=Low
const VALIDATION_TICKET_PRIORITY_MEDIUM = "3";
const VALIDATION_TICKET_STATUS_BACKLOG = "Backlog";

interface ValidationIssueTicketParams {
  validationType: ValidationType;
  groupTitle: string;
  issueTitle: string;
  issueDescription: string;
  appointmentId: string;
  validationId?: string | null;
  issueId?: string;
  /**
   * User-typed feedback (from the Report bug modal). When provided it drives the
   * ticket title and fills the Feedback section; when omitted the ticket falls
   * back to the issue title and a placeholder Feedback prompt.
   */
  userFeedback?: string;
}

export function buildValidationIssueTicketUrl(
  params: ValidationIssueTicketParams,
): string {
  const {
    validationType,
    groupTitle,
    issueTitle,
    issueDescription,
    appointmentId,
    validationId,
    issueId,
    userFeedback,
  } = params;

  const truncatedDescription =
    issueDescription.length > MAX_DESCRIPTION_LENGTH
      ? issueDescription.slice(0, MAX_DESCRIPTION_LENGTH) + "\n\n[truncated]"
      : issueDescription;

  const trimmedFeedback = userFeedback?.trim() ?? "";

  // Title: prefer a snippet of the user's feedback, else the flagged issue title.
  const titleSnippet =
    trimmedFeedback.length > MAX_TITLE_TEXT_LENGTH
      ? trimmedFeedback.slice(0, MAX_TITLE_TEXT_LENGTH).trimEnd() + "…"
      : trimmedFeedback;
  // With feedback: "[MRR validation] {feedback}". Without (e.g. Patient History
  // direct link): fall back to the flagged issue title.
  const title = trimmedFeedback
    ? `[${VALIDATION_TICKET_TITLE_TAG[validationType]}] ${titleSnippet}`
    : `Validation Feedback: ${issueTitle}`;

  const lines: string[] = [
    "## Feedback\n",
    trimmedFeedback ||
      "_Is this a false positive, a missed validation, or something else? Please describe:_",
    "",
    "## Flagged validation issue\n",
  ];

  // The feedback-path title already carries the "[… validation]" tag, so the
  // Type line is redundant there; keep it on the no-feedback direct link.
  if (!trimmedFeedback) {
    lines.push(`- **Type**: ${validationType}`);
  }
  lines.push(
    `- **Group**: ${groupTitle}`,
    `- **Issue**: ${issueTitle}`,
    "",
    "### Issue details\n",
    truncatedDescription,
    "",
    "## Context\n",
    `- Appointment ID: \`${appointmentId}\``,
  );

  if (validationId) {
    lines.push(`- Validation ID: \`${validationId}\``);
  }
  if (issueId) {
    lines.push(`- Issue ID: \`${issueId}\``);
  }

  const queryParams: Record<string, string> = {
    title,
    description: lines.join("\n"),
    project: VALIDATION_TICKET_PROJECT,
    assignee: VALIDATION_TICKET_ASSIGNEES[validationType],
  };
  // Tickets filed via the Report bug modal default to Backlog / Medium.
  if (trimmedFeedback) {
    queryParams.status = VALIDATION_TICKET_STATUS_BACKLOG;
    queryParams.priority = VALIDATION_TICKET_PRIORITY_MEDIUM;
  }

  return `${LINEAR_BASE_URL}?${new URLSearchParams(queryParams).toString()}`;
}

// Impairment-rating bug tickets are tagged "[IR]" and routed to the IR owner.
const IR_TICKET_TITLE_TAG = "IR";
const IR_TICKET_ASSIGNEE = "haoyun";

interface ImpairmentRatingTicketParams {
  appointmentId: string;
  /** Human-readable label for the rating result (e.g. its timestamp). */
  resultLabel: string;
  /** The rating's markdown output, included for context. */
  ratingOutput: string;
  bodyParts?: (string | null)[] | null;
  userFeedback: string;
}

export function buildImpairmentRatingTicketUrl(
  params: ImpairmentRatingTicketParams,
): string {
  const { appointmentId, resultLabel, ratingOutput, bodyParts, userFeedback } =
    params;

  const trimmedFeedback = userFeedback.trim();
  const titleSnippet =
    trimmedFeedback.length > MAX_TITLE_TEXT_LENGTH
      ? trimmedFeedback.slice(0, MAX_TITLE_TEXT_LENGTH).trimEnd() + "…"
      : trimmedFeedback;
  const title = `[${IR_TICKET_TITLE_TAG}] ${titleSnippet}`;

  const truncatedOutput =
    ratingOutput.length > MAX_DESCRIPTION_LENGTH
      ? ratingOutput.slice(0, MAX_DESCRIPTION_LENGTH) + "\n\n[truncated]"
      : ratingOutput;

  const bodyPartsLabel = (bodyParts ?? [])
    .filter((part): part is string => !!part)
    .join(", ");

  const lines: string[] = [
    "## Feedback\n",
    trimmedFeedback || "_Please describe the problem with this rating:_",
    "",
    "## Rating result\n",
    `- **Result**: ${resultLabel}`,
  ];
  if (bodyPartsLabel) {
    lines.push(`- **Body parts**: ${bodyPartsLabel}`);
  }
  lines.push(
    "",
    "### Rating output\n",
    truncatedOutput || "_No rating output._",
    "",
    "## Context\n",
    `- Appointment ID: \`${appointmentId}\``,
  );

  const queryParams: Record<string, string> = {
    title,
    description: lines.join("\n"),
    project: VALIDATION_TICKET_PROJECT,
    assignee: IR_TICKET_ASSIGNEE,
    status: VALIDATION_TICKET_STATUS_BACKLOG,
    priority: VALIDATION_TICKET_PRIORITY_MEDIUM,
  };

  return `${LINEAR_BASE_URL}?${new URLSearchParams(queryParams).toString()}`;
}

interface ValidationIssueBatchEntry {
  groupTitle: string;
  issueTitle: string;
  issueDescription: string;
  issueId?: string;
}

interface ValidationBatchTicketParams {
  validationType: ValidationType;
  issues: ValidationIssueBatchEntry[];
  appointmentId: string;
  validationId?: string | null;
}

const MAX_BATCH_DESCRIPTION_LENGTH = 4000;

export function buildValidationBatchTicketUrl(
  params: ValidationBatchTicketParams,
): string {
  const { validationType, issues, appointmentId, validationId } = params;

  const lines: string[] = [`## Flagged validation issues (${issues.length})\n`];

  for (let i = 0; i < issues.length; i++) {
    const issue = issues[i];
    const desc = issue.issueDescription || "No additional details provided.";
    const truncatedDesc =
      desc.length > MAX_DESCRIPTION_LENGTH
        ? desc.slice(0, MAX_DESCRIPTION_LENGTH) + "\n\n[truncated]"
        : desc;
    lines.push(`### ${issue.groupTitle}: ${issue.issueTitle}\n`);
    lines.push(truncatedDesc);
    if (issue.issueId) {
      lines.push(`\n_Issue ID: \`${issue.issueId}\`_`);
    }
    lines.push("");
    lines.push(
      "**Feedback**: _Is this a false positive, a missed validation, or something else? Please describe:_\n",
    );
    if (i < issues.length - 1) {
      lines.push("\n---\n");
    }
  }

  lines.push("\n---\n");
  lines.push("## Context\n");
  lines.push(`- **Type**: ${validationType}`);
  lines.push(`- Appointment ID: \`${appointmentId}\``);
  if (validationId) {
    lines.push(`- Validation ID: \`${validationId}\``);
  }

  let description = lines.join("\n");
  if (description.length > MAX_BATCH_DESCRIPTION_LENGTH) {
    description =
      description.slice(0, MAX_BATCH_DESCRIPTION_LENGTH) + "\n\n[truncated]";
  }

  const searchParams = new URLSearchParams({
    title: `${validationType} Validation Feedback (${issues.length} issues)`,
    description,
    project: VALIDATION_TICKET_PROJECT,
    assignee: VALIDATION_TICKET_ASSIGNEES[validationType],
  });

  return `${LINEAR_BASE_URL}?${searchParams.toString()}`;
}
